import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import {
  Receipt, RefreshCw, ArrowLeft, Calendar, FileText, Layers, Activity, AlertCircle,
} from "lucide-react";

const fmt = (n) => Number(n || 0).toLocaleString("en-US", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
});

const StatusPill = ({ s }) => {
  const cls = {
    posted: "bg-emerald-50 text-emerald-700 border-emerald-200",
    draft:  "bg-amber-50 text-amber-700 border-amber-200",
  }[s] || "bg-slate-50 text-slate-600 border-slate-200";
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${cls}`}
      data-testid={`tax-adj-status-${s}`}>{s || "—"}</span>
  );
};

export default function AccountantTaxAdjustments() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(50);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    setErr(null);
    try {
      const r = await axiosInstance.get(`/accountant-workspace/tax-adjustments?limit=${limit}`);
      setData(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "تعذّر تحميل قائمة التعديلات الضريبية";
      setErr(msg);
      toast.error(msg);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [limit]);

  const totals = useMemo(() => {
    if (!data?.items) return { count: 0, sum: 0, posted: 0, draft: 0 };
    let sum = 0, posted = 0, draft = 0;
    for (const i of data.items) {
      sum += Number(i.amount || 0);
      if (i.status === "posted") posted++;
      else if (i.status === "draft") draft++;
    }
    return { count: data.items.length, sum, posted, draft };
  }, [data]);

  return (
    <div data-testid="tax-adjustments-page" className="space-y-5 p-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <button data-testid="tax-adj-back"
            onClick={() => navigate("/external-accountant")}
            className="rounded-lg border border-slate-300 bg-white p-2 hover:bg-slate-50">
            <ArrowLeft className="h-4 w-4" />
          </button>
          <Receipt className="h-6 w-6 text-amber-600" />
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Tax Adjustments</h1>
            <p className="text-xs text-slate-500">
              قائمة موحّدة من طبقة التجريد · مصدر:{" "}
              <span className="font-mono">{data?.source || "—"}</span>
              {data?.count !== undefined && (
                <span className="ml-2 rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-600">
                  {data.count} {data.count === 1 ? "قيد" : "قيود"}
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="tax-adj-limit" value={limit}
            onChange={(e) => setLimit(Number(e.target.value))}
            className="rounded-lg border border-slate-300 bg-white px-2 py-1.5 text-xs">
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>
          <button data-testid="tax-adj-refresh" onClick={() => load(true)}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
            <RefreshCw className="h-4 w-4" /> تحديث
          </button>
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Layers className="h-3.5 w-3.5" /> إجمالي القيود
          </div>
          <div className="mt-1 font-mono text-2xl font-bold text-slate-900"
            data-testid="tax-adj-total-count">{totals.count}</div>
        </div>
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
          <div className="flex items-center gap-2 text-xs text-emerald-700">مرحَّلة</div>
          <div className="mt-1 font-mono text-2xl font-bold text-emerald-700"
            data-testid="tax-adj-posted">{totals.posted}</div>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-4">
          <div className="flex items-center gap-2 text-xs text-amber-700">مسوّدات</div>
          <div className="mt-1 font-mono text-2xl font-bold text-amber-700"
            data-testid="tax-adj-draft">{totals.draft}</div>
        </div>
        <div className="rounded-xl border border-violet-200 bg-violet-50/50 p-4">
          <div className="flex items-center gap-2 text-xs text-violet-700">إجمالي المبالغ</div>
          <div className="mt-1 font-mono text-2xl font-bold text-violet-700"
            data-testid="tax-adj-total-amount">{fmt(totals.sum)} <span className="text-sm">SAR</span></div>
        </div>
      </div>

      {/* Body */}
      {loading ? (
        <div className="p-12 text-center text-slate-400" data-testid="tax-adj-loading">
          <Activity className="mx-auto h-6 w-6 animate-pulse" />
          <div className="mt-2 text-sm">جارٍ التحميل…</div>
        </div>
      ) : err ? (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-5" data-testid="tax-adj-error">
          <div className="flex items-center gap-2 font-bold text-rose-800">
            <AlertCircle className="h-4 w-4" /> فشل في تحميل البيانات
          </div>
          <p className="mt-2 text-sm text-rose-700">{err}</p>
          <button onClick={() => load()}
            className="mt-3 rounded border border-rose-300 bg-white px-3 py-1.5 text-xs font-medium text-rose-700 hover:bg-rose-100">
            إعادة المحاولة
          </button>
        </div>
      ) : !data?.items?.length ? (
        <div className="rounded-xl border border-slate-200 bg-white p-10 text-center text-sm text-slate-500"
          data-testid="tax-adj-empty">
          <Receipt className="mx-auto mb-2 h-8 w-8 text-slate-300" />
          لا توجد قيود ضريبية بعد · أنشِئ إقراراً ضريبياً أو قيد تعديل لتظهر هنا.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="min-w-full text-sm" data-testid="tax-adj-table">
            <thead className="bg-slate-50/80 text-xs uppercase text-slate-600">
              <tr>
                <th className="px-4 py-2.5 text-left"><Calendar className="inline h-3 w-3" /> Date</th>
                <th className="px-4 py-2.5 text-left">Reference</th>
                <th className="px-4 py-2.5 text-left">Type</th>
                <th className="px-4 py-2.5 text-left">Description</th>
                <th className="px-4 py-2.5 text-right">Amount</th>
                <th className="px-4 py-2.5 text-right">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((row, i) => (
                <tr key={row.id || i}
                  className="border-t border-slate-100 hover:bg-amber-50/40"
                  data-testid={`tax-adj-row-${i}`}>
                  <td className="px-4 py-2 font-mono text-xs text-slate-600">
                    {(row.date || "").slice(0, 10) || "—"}
                  </td>
                  <td className="px-4 py-2 font-mono text-xs">
                    <button onClick={() => navigate(`/journal?ref=${encodeURIComponent(row.reference || row.id)}`)}
                      className="text-amber-700 hover:underline" title="افتح القيد">
                      {row.reference || "—"}
                    </button>
                  </td>
                  <td className="px-4 py-2"><span className="rounded bg-slate-100 px-2 py-0.5 text-[10px] font-medium text-slate-700">{row.type || "vat_adjustment"}</span></td>
                  <td className="px-4 py-2 text-slate-700 max-w-md truncate" title={row.description}>
                    <FileText className="mr-1 inline h-3 w-3 text-slate-400" />
                    {row.description || "—"}
                  </td>
                  <td className="px-4 py-2 text-right font-mono">{fmt(row.amount)} SAR</td>
                  <td className="px-4 py-2 text-right"><StatusPill s={row.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <p className="text-[11px] text-slate-400" data-testid="tax-adj-source-note">
        مصدر البيانات يُقرأ حالياً من طبقة التجريد (<code className="rounded bg-slate-100 px-1">{data?.source || "journals_abstraction"}</code>) فوق
        مجموعة الـ Journals. عند إنشاء مجموعة <code className="rounded bg-slate-100 px-1">tax_adjustments</code> مخصّصة لاحقاً، لن يتغيّر شكل الاستجابة.
      </p>
    </div>
  );
}
